import { useCallback, useRef } from "react";

// Default schedule: 2_000, 4_000, 8_000, 16_000, 30_000 (capped) → ~9 calls
// in 5 minutes vs ~300 at a fixed 1 s. Tuned for the Cloudflare WAF blocks
// reported in FR-141.
const DEFAULT_START_MS = 2_000;
const DEFAULT_MAX_MS = 30_000;
const DEFAULT_BUDGET_MS = 5 * 60 * 1_000;

export type UsePollingBackoffOptions<T> = {
  shouldStop: (data: T | undefined) => boolean;
  startMs?: number;
  maxMs?: number;
  budgetMs?: number;
  /**
   * Fired exactly once per polling session when the wall-clock budget
   * (`budgetMs`) is exhausted. A "session" starts on the first call where
   * `shouldStop` returns false and ends on the next `shouldStop=true`
   * transition (which also re-arms the gate). The latest callback is read
   * via a ref, so callers do not need to memoize.
   *
   * Intended for observability (e.g. `Sentry.captureMessage`). The hook
   * stays free of any specific reporting dependency by delegating to the
   * caller — different call sites can tag the event distinctly.
   */
  onBudgetExhausted?: () => void;
};

/**
 * Returns a function suitable for SWR's `refreshInterval` that yields an
 * exponentially-increasing polling interval (capped at `maxMs`) and stops
 * after `budgetMs` of wall-clock time has elapsed since the first call of a
 * polling session. The callback resets back to `startMs` whenever
 * `shouldStop(data)` returns `true`, so a stop → resume cycle starts fresh.
 *
 * The latest `shouldStop` is read through a ref each invocation, so callers
 * are not required to memoize their predicate. SWR holds onto the returned
 * function for the lifetime of the SWR cache key, so closing over fresh
 * render state via a ref is what makes the stop condition stay current.
 *
 * IMPORTANT — START-CONDITION GOTCHA: SWR only re-evaluates `refreshInterval`
 * AFTER a fetch completes. The returned function's reference is intentionally
 * stable. If `shouldStop` returns `true` on the FIRST call (e.g. the start
 * condition depends on a user-action boolean that is `false` at mount), SWR
 * will not schedule a timer, and the predicate alone will NOT re-trigger
 * scheduling when the start condition later flips to `true` — there is no
 * fetch in between to invoke this callback. Use this hook to drive the
 * IN-CYCLE cadence and the IN-CYCLE stop (e.g. data has reached a terminal
 * state); enforce START conditions at the SWR config level instead:
 *
 *   const config = useMemo(
 *     () => startCondition ? { refreshInterval: backoff } : {},
 *     [startCondition, backoff],
 *   );
 *   useSWR(key, fetcher, config);
 *
 * Toggling the SWR config object between `{}` and `{ refreshInterval }` is
 * what gives SWR a config-change to react to.
 */
export function usePollingBackoff<T = unknown>({
  shouldStop,
  startMs = DEFAULT_START_MS,
  maxMs = DEFAULT_MAX_MS,
  budgetMs = DEFAULT_BUDGET_MS,
  onBudgetExhausted,
}: UsePollingBackoffOptions<T>): (data: T | undefined) => number {
  const shouldStopRef = useRef(shouldStop);
  shouldStopRef.current = shouldStop;

  const onBudgetExhaustedRef = useRef(onBudgetExhausted);
  onBudgetExhaustedRef.current = onBudgetExhausted;

  const startedAtRef = useRef<number | null>(null);
  const attemptRef = useRef(0);
  const hasReportedBudgetExhaustedRef = useRef(false);

  return useCallback(
    (data) => {
      if (shouldStopRef.current(data)) {
        startedAtRef.current = null;
        attemptRef.current = 0;
        hasReportedBudgetExhaustedRef.current = false;
        return 0;
      }
      if (startedAtRef.current === null) {
        startedAtRef.current = Date.now();
      }
      if (Date.now() - startedAtRef.current > budgetMs) {
        // Intentional: once the wall-clock budget is exhausted we return 0
        // WITHOUT resetting `startedAtRef` / `attemptRef`. That makes the
        // budget a hard stop on auto-polling — the only way to resume
        // auto-polling is a `shouldStop=true` transition (e.g. data
        // settles into a terminal state) which resets state in the branch
        // above. SWR's `revalidateOnFocus` / `revalidateOnReconnect` /
        // manual `mutate()` are unaffected and still give callers a
        // one-shot refresh path if the user comes back to the tab.
        if (!hasReportedBudgetExhaustedRef.current) {
          hasReportedBudgetExhaustedRef.current = true;
          onBudgetExhaustedRef.current?.();
        }
        return 0;
      }
      const interval = Math.min(maxMs, startMs * 2 ** attemptRef.current);
      attemptRef.current += 1;
      return interval;
    },
    [startMs, maxMs, budgetMs]
  );
}
